import { apiHelpers } from './api';
import type {
  MediaRecommendation,
  MediaComment,
  MediaFilters,
  CreateMediaRecommendationInput,
  UpdateMediaRecommendationInput,
  RateMediaInput,
  AddMediaCommentInput,
  PaginatedMediaResponse,
  PaginatedMediaCommentsResponse,
} from '@/types/mediaRecommendation.types';

const BASE_URL = '/media-recommendations';

export const mediaRecommendationService = {
  // Get all media recommendations with optional filters
  async getRecommendations(filters?: MediaFilters): Promise<PaginatedMediaResponse> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.type) params.append('type', filters.type);
    if (filters?.genre) params.append('genre', filters.genre);
    if (filters?.addedBy) params.append('addedBy', filters.addedBy);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
    return apiHelpers.get<PaginatedMediaResponse>(url);
  },

  // Get single media recommendation by ID
  async getRecommendationById(id: string): Promise<MediaRecommendation> {
    return apiHelpers.get<MediaRecommendation>(`${BASE_URL}/${id}`);
  },

  // Create new media recommendation
  async createRecommendation(data: CreateMediaRecommendationInput): Promise<MediaRecommendation> {
    return apiHelpers.post<MediaRecommendation>(BASE_URL, data);
  },

  // Update media recommendation
  async updateRecommendation(id: string, data: UpdateMediaRecommendationInput): Promise<MediaRecommendation> {
    return apiHelpers.put<MediaRecommendation>(`${BASE_URL}/${id}`, data);
  },

  // Delete media recommendation
  async deleteRecommendation(id: string): Promise<void> {
    return apiHelpers.delete(`${BASE_URL}/${id}`);
  },

  // Rate a media recommendation (1-10)
  async rateMedia(id: string, data: RateMediaInput): Promise<MediaRecommendation> {
    return apiHelpers.post<MediaRecommendation>(`${BASE_URL}/${id}/rate`, data);
  },

  // Remove rating from media
  async removeRating(id: string): Promise<MediaRecommendation> {
    return apiHelpers.delete<MediaRecommendation>(`${BASE_URL}/${id}/rate`);
  },

  // Get comments for a media recommendation
  async getComments(mediaId: string, page = 1, limit = 20): Promise<PaginatedMediaCommentsResponse> {
    return apiHelpers.get<PaginatedMediaCommentsResponse>(
      `${BASE_URL}/${mediaId}/comments?page=${page}&limit=${limit}`
    );
  },

  // Add comment to media
  async addComment(mediaId: string, data: AddMediaCommentInput): Promise<MediaComment> {
    return apiHelpers.post<MediaComment>(`${BASE_URL}/${mediaId}/comments`, data);
  },

  // Delete comment
  async deleteComment(mediaId: string, commentId: string): Promise<void> {
    return apiHelpers.delete(`${BASE_URL}/${mediaId}/comments/${commentId}`);
  },
};

export default mediaRecommendationService;
